// import { useParams } from "react-router-dom";
// import { Box, Button, CircularProgress, Typography } from "@mui/material";
// import { useEffect, useState } from "react";
// import { getInvoiceFile } from "../config/getInvoiceFile";


// export default function InvoicePage() {
//   const { guestId } = useParams(); // URL like /:guestId
//   const [invoiceUrl, setInvoiceUrl] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     if (!guestId) {
//       setError("Invalid invoice request");
//       setLoading(false);
//       return;
//     }

//     async function fetchInvoice() {
//       try {
//         // 👇 Reuse getFile (you can ignore grpUserId & hostelId for invoices)
//         const url = await getInvoiceFile(`guest_invoice/${guestId}`);
//         if (url) {
//           setInvoiceUrl(url);
//         } else {
//           setError("Invoice not found");
//         }
//       } catch (err) {
//         console.error("Error fetching invoice:", err);
//         setError("Error fetching invoice");
//       } finally {
//         setLoading(false);
//       }
//     }

//     fetchInvoice();
//   }, [guestId]);

//   if (loading) {
//     return (
//       <Box
//         display="flex"
//         justifyContent="center"
//         alignItems="center"
//         height="300px"
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   if (error) {
//     return (
//       <Box textAlign="center" mt={4}>
//         <Typography color="error">{error}</Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box>
//       {/* Show Invoice */}
//       <iframe
//         src={invoiceUrl}
//         title="Invoice"
//         style={{ width: "100%", height: "100vh", border: "none" }}
//       />
//       <Box textAlign="center" mt={2}>
//         <Button
//           variant="contained"
//           color="primary"
//           href={invoiceUrl}
//           target="_blank"
//           rel="noopener noreferrer"
//         >
//           Download Invoice
//         </Button>
//       </Box>
//     </Box>
//   );
// }


import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useMemo, useRef, useState } from "react";
import { getInvoiceFile } from "../config/getInvoiceFile";

export default function InvoicePage() {
  const { guestId } = useParams();
  const navigate = useNavigate();
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Optional metadata fetched via HEAD once we have a URL
  const [meta, setMeta] = useState({
    filename: null,
    lastModified: null,
    sizeBytes: null,
  });

  const iframeRef = useRef(null);

  const prettySize = useMemo(() => {
    if (!meta.sizeBytes && meta.sizeBytes !== 0) return null;
    const i = Math.floor(Math.log(meta.sizeBytes) / Math.log(1024));
    const value = (meta.sizeBytes / Math.pow(1024, i)).toFixed(1);
    return `${value} ${["B", "KB", "MB", "GB", "TB"][i]}`;
  }, [meta.sizeBytes]);

  useEffect(() => {
    if (!guestId) {
      setError("Invalid invoice request.");
      setLoading(false);
      return;
    }
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId]);

  async function fetchInvoice(isRefresh = false) {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);

      // Get a signed/viewable URL for the invoice
      const url = await getInvoiceFile(`guest_invoice/${guestId}`);
      if (!url) {
        setError("Invoice not found.");
        setInvoiceUrl(null);
        return;
      }
      setInvoiceUrl(url);

      // Try to fetch metadata (optional; ignore failures)
      try {
        const res = await fetch(url, { method: "HEAD" });
        const cd = res.headers.get("content-disposition") || "";
        // attempt to parse filename
        let filename = null;
        const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1]);
        }
        const lastModified = res.headers.get("last-modified");
        const size = res.headers.get("content-length");
        setMeta({
          filename: filename || `invoice-${guestId}.pdf`,
          lastModified: lastModified ? new Date(lastModified).toString() : null,
          sizeBytes: size ? Number(size) : null,
        });
      } catch {
        // silently ignore meta errors
        setMeta({
          filename: `invoice-${guestId}.pdf`,
          lastModified: null,
          sizeBytes: null,
        });
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
      setError("Error fetching invoice.");
      setInvoiceUrl(null);
    } finally {
      isRefresh ? setRefreshing(false) : setLoading(false);
    }
  }

  const handleDownload = () => {
    if (!invoiceUrl) return;
    const a = document.createElement("a");
    a.href = invoiceUrl;
    // A best-effort download name if same-origin or CD header not present
    a.download = meta.filename || `invoice-${guestId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenNewTab = () => {
    if (!invoiceUrl) return;
    window.open(invoiceUrl, "_blank", "noopener,noreferrer");
  };

  const handlePrint = () => {
    // Print the iframe content if same-origin; otherwise open new tab to print
    const iframe = iframeRef.current;
    try {
      if (iframe?.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } else {
        handleOpenNewTab();
      }
    } catch {
      // Cross-origin PDFs can’t be printed from iframe programmatically
      handleOpenNewTab();
    }
  };

  const Toolbar = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      p={1}
      sx={{ px: { xs: 1, sm: 2 } }}
    >
      <Tooltip title="Back">
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>

      <Typography
        variant="h6"
        noWrap
        sx={{
          flex: 1,
          fontSize: { xs: "1rem", sm: "1.25rem" }, // smaller on phones
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        Invoice {guestId ? `#${guestId}` : ""}
      </Typography>

      {meta.filename && (
        <Chip
          variant="outlined"
          label={meta.filename}
          sx={{ display: { xs: "none", md: "inline-flex" } }}
        />
      )}

      <Tooltip title="Refresh">
        <span>
          <IconButton onClick={() => fetchInvoice(true)} disabled={refreshing}>
            <RefreshIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Download">
        <span>
          <IconButton onClick={handleDownload} disabled={!invoiceUrl}>
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>


      <Tooltip title="Open in new tab">
        <span>
          <IconButton onClick={handleOpenNewTab} disabled={!invoiceUrl}>
            <OpenInNewIcon /> 
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2 }}>
        {Toolbar}
        <LinearProgress />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="60vh"
        >
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 2 }}>
        {Toolbar}
        <Box textAlign="center" mt={6}>
          <Typography color="error" variant="body1" mb={2}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => fetchInvoice(true)}>
            Try Again
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{ p: 0, height: "100vh", display: "flex", flexDirection: "column" }}
    >
      {Toolbar}
      <Divider />
      {/* Meta strip */}
      {(meta.lastModified || prettySize) && (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            {prettySize && (
              <Typography variant="body2" color="text.secondary">
                Size: {prettySize}
              </Typography>
            )}
            {meta.lastModified && (
              <Typography variant="body2" color="text.secondary">
                Updated: {new Date(meta.lastModified).toLocaleString()}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
      {/* PDF viewer area */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {/* Keep iframe for native PDF viewer; modern attributes below */}
        <iframe
          ref={iframeRef}
          src={invoiceUrl}
          title="Invoice PDF"
          loading="lazy"
          referrerPolicy="no-referrer"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
          }}
        />
      </Box>
      {/* Bottom actions for mobile reachability */}
      {/* <Box
        sx={{
          p: 1.5,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          display: { xs: "block", md: "none" },
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => fetchInvoice(true)}
          >
            Refresh
          </Button>
          <Button fullWidth variant="outlined" onClick={handleOpenNewTab}>
            Open
          </Button>
          <Button fullWidth variant="contained" onClick={handleDownload}>
            Download
          </Button>
        </Stack>
      </Box> */}
    </Paper>
  );
}
